import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Webhook,
  Plus,
  Loader2,
  Trash2,
  Copy,
  Check,
  AlertTriangle,
  X,
  Send,
  RotateCw,
  ChevronDown,
  ChevronUp,
  CircleCheck,
  CircleAlert,
} from "lucide-react";

/**
 * Team-tier outbound webhooks card.
 * Mirrors the ApiKeysCard pattern: create / list / patch / revoke,
 * with a one-time signing-secret reveal modal + per-webhook deliveries drawer.
 */
export default function WebhooksCard() {
  const [eventCatalog, setEventCatalog] = useState([]);
  const [hooks, setHooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", url: "", events: [] });
  const [reveal, setReveal] = useState(null); // {secret, name, rotated?}
  const [gateMessage, setGateMessage] = useState(null);
  const [expanded, setExpanded] = useState(null); // webhook id

  const loadCatalog = useCallback(async () => {
    try {
      const r = await api.get("/webhooks/events/catalog");
      setEventCatalog(r.data || []);
    } catch (err) {
      if (err?.response?.status === 403) {
        setGateMessage(err?.response?.data?.detail || "Upgrade to Team to unlock webhooks.");
      }
    }
  }, []);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/webhooks");
      setHooks(r.data || []);
      setGateMessage(null);
    } catch (err) {
      if (err?.response?.status === 403) {
        setGateMessage(err?.response?.data?.detail || "Upgrade to Team to unlock webhooks.");
      } else {
        toast.error(err?.response?.data?.detail || "Failed to load webhooks");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadCatalog(); refresh(); }, [loadCatalog, refresh]);

  const toggleEventInForm = (ev) => {
    setForm((prev) => ({
      ...prev,
      events: prev.events.includes(ev)
        ? prev.events.filter((e) => e !== ev)
        : [...prev.events, ev],
    }));
  };

  const create = async (e) => {
    e?.preventDefault?.();
    if (!form.name.trim()) return toast.error("Give the webhook a name");
    if (!form.url.trim()) return toast.error("Receiver URL is required");
    if (!/^https?:\/\//i.test(form.url)) return toast.error("URL must start with http(s)://");
    if (form.events.length === 0) return toast.error("Pick at least one event");

    setCreating(true);
    try {
      const r = await api.post("/webhooks", form);
      setReveal({ secret: r.data.signing_secret, name: r.data.name, rotated: false });
      setForm({ name: "", url: "", events: [] });
      setShowForm(false);
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create webhook");
    } finally {
      setCreating(false);
    }
  };

  const patchHook = async (id, patch) => {
    try {
      await api.patch(`/webhooks/${id}`, patch);
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    }
  };

  const sendTest = async (id) => {
    try {
      const r = await api.post(`/webhooks/${id}/test`);
      toast.success(`Test event dispatched to ${r.data.url}`);
      setTimeout(refresh, 2500);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Test send failed");
    }
  };

  const rotate = async (id, name) => {
    if (!window.confirm("Rotate signing secret? Your receiver will reject signatures until you update the secret on their side.")) return;
    try {
      const r = await api.post(`/webhooks/${id}/rotate-secret`);
      setReveal({ secret: r.data.signing_secret, name, rotated: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Rotation failed");
    }
  };

  const remove = async (h) => {
    if (!window.confirm(`Delete "${h.name}"? Subscribed events will stop firing immediately.`)) return;
    try {
      await api.delete(`/webhooks/${h.id}`);
      toast.success("Webhook deleted");
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  if (gateMessage) {
    return (
      <div className="card" data-testid="webhooks-card-gated">
        <div className="label-eyebrow flex items-center gap-1.5 mb-2"><Webhook size={11} /> Outbound webhooks</div>
        <div className="p-4 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-900 flex items-start gap-2">
          <AlertTriangle size={16} className="shrink-0 mt-0.5" />
          <div>{gateMessage}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="card" data-testid="webhooks-card">
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="label-eyebrow flex items-center gap-1.5"><Webhook size={11} /> Outbound webhooks</div>
        {!showForm && (
          <button
            type="button"
            className="text-xs text-[var(--primary)] inline-flex items-center gap-1 hover:underline whitespace-nowrap font-semibold"
            onClick={() => setShowForm(true)}
            data-testid="webhooks-show-form-btn"
          >
            <Plus size={11} /> New webhook
          </button>
        )}
      </div>
      <p className="text-sm text-[var(--ink-2)] mb-3 leading-relaxed">
        Push events to your CRM, Zap, or backend the moment they happen. Each event is signed with
        HMAC-SHA256 (header <code className="font-mono text-xs bg-slate-100 px-1 rounded">X-IST-Signature</code>) so you can verify it really came from us.
      </p>

      {showForm && (
        <form onSubmit={create} className="border border-slate-200 rounded-lg p-3 mb-3 space-y-2.5" data-testid="webhooks-create-form">
          <div>
            <label className="text-[10px] uppercase tracking-wide text-[var(--ink-3)] font-bold block mb-1">Name</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g. Zapier — new GO leads"
              maxLength={80}
              className="w-full px-2 py-1.5 rounded-lg border border-slate-200 text-sm focus:outline-none focus:border-[var(--primary)]"
              data-testid="webhook-name-input"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wide text-[var(--ink-3)] font-bold block mb-1">Receiver URL</label>
            <input
              type="url"
              value={form.url}
              onChange={(e) => setForm({ ...form, url: e.target.value })}
              placeholder="https://hooks.zapier.com/hooks/catch/xxx/yyy"
              className="w-full px-2 py-1.5 rounded-lg border border-slate-200 text-sm font-mono focus:outline-none focus:border-[var(--primary)]"
              data-testid="webhook-url-input"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wide text-[var(--ink-3)] font-bold block mb-1.5">Events</label>
            <div className="space-y-1.5">
              {eventCatalog.map((ev) => (
                <label key={ev.event} className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.events.includes(ev.event)}
                    onChange={() => toggleEventInForm(ev.event)}
                    className="mt-1 accent-[var(--primary)]"
                    data-testid={`webhook-event-${ev.event.replace(".", "-")}`}
                  />
                  <span className="text-sm leading-tight">
                    <code className="font-mono text-xs">{ev.event}</code>
                    <span className="block text-xs text-[var(--ink-3)] leading-snug">{ev.description}</span>
                  </span>
                </label>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-2 pt-1">
            <button type="submit" className="btn btn-primary" disabled={creating} data-testid="webhook-create-btn">
              {creating ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
              Create webhook
            </button>
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() => { setShowForm(false); setForm({ name: "", url: "", events: [] }); }}
              data-testid="webhook-cancel-btn"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <div className="text-center text-sm text-[var(--ink-3)] py-4">
          <Loader2 size={14} className="animate-spin inline" /> Loading…
        </div>
      ) : hooks.length === 0 ? (
        <div className="text-center text-sm text-[var(--ink-3)] py-4">
          No webhooks yet. Click <strong>New webhook</strong> to subscribe to events.
        </div>
      ) : (
        <ul className="space-y-1" data-testid="webhooks-list">
          {hooks.map((h) => (
            <li key={h.id} className={"border-b last:border-b-0 py-2 " + (h.enabled ? "" : "opacity-60")} data-testid={`webhook-row-${h.id}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="font-semibold flex items-center gap-1.5 flex-wrap text-sm">
                    {h.name}
                    <StatusChip hook={h} />
                  </div>
                  <div className="text-[10px] text-[var(--ink-3)] font-mono mt-0.5 break-all">{h.url}</div>
                  <div className="text-[10px] text-[var(--ink-3)] mt-0.5 flex items-center gap-1 flex-wrap">
                    {(h.events || []).map((e) => (
                      <code key={e} className="font-mono bg-slate-100 px-1 rounded">{e}</code>
                    ))}
                  </div>
                </div>
                <div className="flex items-center gap-0.5 shrink-0">
                  <IconBtn label="Test send" onClick={() => sendTest(h.id)} icon={Send} testId={`webhook-test-${h.id}`} />
                  <IconBtn
                    label={h.enabled ? "Disable" : "Enable"}
                    onClick={() => patchHook(h.id, { enabled: !h.enabled })}
                    icon={h.enabled ? CircleCheck : CircleAlert}
                    testId={`webhook-toggle-${h.id}`}
                    color={h.enabled ? "text-emerald-600" : "text-slate-400"}
                  />
                  <IconBtn label="Rotate secret" onClick={() => rotate(h.id, h.name)} icon={RotateCw} testId={`webhook-rotate-${h.id}`} />
                  <IconBtn label="Delete" onClick={() => remove(h)} icon={Trash2} testId={`webhook-delete-${h.id}`} color="text-rose-600" />
                  <IconBtn
                    label={expanded === h.id ? "Hide deliveries" : "Show deliveries"}
                    onClick={() => setExpanded((cur) => (cur === h.id ? null : h.id))}
                    icon={expanded === h.id ? ChevronUp : ChevronDown}
                    testId={`webhook-deliveries-${h.id}`}
                  />
                </div>
              </div>
              {expanded === h.id && <DeliveriesPanel webhookId={h.id} />}
            </li>
          ))}
        </ul>
      )}

      {reveal && <SecretRevealModal secret={reveal.secret} name={reveal.name} rotated={reveal.rotated} onClose={() => setReveal(null)} />}
    </div>
  );
}

function StatusChip({ hook }) {
  if (!hook.enabled) {
    return <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-slate-200 text-slate-700 uppercase">Disabled</span>;
  }
  if (hook.failure_count >= 3) {
    return <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-rose-100 text-rose-700 uppercase">{hook.failure_count} fails</span>;
  }
  if (hook.last_status_code && (hook.last_status_code < 200 || hook.last_status_code >= 300)) {
    return <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-amber-100 text-amber-700 uppercase">HTTP {hook.last_status_code}</span>;
  }
  if (hook.last_status_code) {
    return <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-emerald-100 text-emerald-700 uppercase">OK</span>;
  }
  return <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-sky-100 text-sky-700 uppercase">Idle</span>;
}

function IconBtn({ label, onClick, icon: Icon, testId, color = "text-[var(--ink-2)]" }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      aria-label={label}
      className={`p-1.5 rounded hover:bg-slate-100 transition-colors ${color}`}
      data-testid={testId}
    >
      <Icon size={13} />
    </button>
  );
}

function DeliveriesPanel({ webhookId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/webhooks/${webhookId}/deliveries`);
      setData(r.data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [webhookId]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="mt-2 p-2 text-xs text-[var(--ink-3)]">
        <Loader2 size={12} className="animate-spin inline" /> Loading deliveries…
      </div>
    );
  }
  const deliveries = data?.deliveries || [];
  return (
    <div className="mt-2 bg-slate-50 rounded-lg p-2 border border-slate-200" data-testid={`deliveries-panel-${webhookId}`}>
      <div className="flex items-center justify-between mb-1.5">
        <div className="text-[10px] uppercase tracking-wide text-[var(--ink-3)] font-bold">Last {deliveries.length} deliveries</div>
        <button type="button" onClick={load} className="text-[10px] text-[var(--primary)] hover:underline" data-testid={`deliveries-refresh-${webhookId}`}>
          Refresh
        </button>
      </div>
      {deliveries.length === 0 ? (
        <div className="text-xs text-[var(--ink-3)] py-2 text-center">No deliveries yet. Trigger an event or send a test.</div>
      ) : (
        <ul className="space-y-1 max-h-48 overflow-y-auto">
          {deliveries.map((d) => (
            <li key={d.id} className="text-[11px] flex items-start gap-1.5 py-0.5">
              <StatusDot ok={d.success} />
              <code className="font-mono text-[10px] bg-white px-1 rounded shrink-0">{d.event}</code>
              <span className="text-[var(--ink-2)] shrink-0">{d.status_code || (d.error || "0")}</span>
              <span className="text-[var(--ink-3)] ml-auto whitespace-nowrap">
                {d.fired_at ? new Date(d.fired_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" }) : ""}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function StatusDot({ ok }) {
  return (
    <span className={`mt-0.5 w-2 h-2 rounded-full shrink-0 ${ok ? "bg-emerald-500" : "bg-rose-500"}`} aria-hidden />
  );
}

function SecretRevealModal({ secret, name, rotated, onClose }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(secret);
      setCopied(true);
      toast.success("Copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Copy failed");
    }
  };
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="webhook-reveal-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg">
        <div className="p-4 border-b flex items-center justify-between">
          <div>
            <div className="font-display font-extrabold text-lg">
              {rotated ? "New signing secret" : "Your webhook signing secret"}
            </div>
            <div className="text-xs text-[var(--ink-2)] mt-0.5">{name}</div>
          </div>
          <button type="button" onClick={onClose} className="p-1 hover:bg-slate-100 rounded">
            <X size={16} />
          </button>
        </div>
        <div className="p-4">
          <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-900 flex items-start gap-2 mb-3">
            <AlertTriangle size={14} className="shrink-0 mt-0.5" />
            <div>
              <strong>{rotated ? "Update your receiver immediately." : "This is the only time you'll see this secret."}</strong>{" "}
              Use it to verify the <code className="font-mono bg-white px-1 rounded">X-IST-Signature</code> header on incoming requests:
              <code className="font-mono text-[11px] block mt-1 leading-snug">sha256=&lt;HMAC-SHA256(secret, raw_body)&gt;</code>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <code className="flex-1 font-mono text-xs bg-slate-900 text-slate-100 px-3 py-2 rounded break-all" data-testid="webhook-secret-raw">{secret}</code>
            <button type="button" className="btn btn-primary" onClick={copy} data-testid="webhook-secret-copy-btn">
              {copied ? <Check size={14} /> : <Copy size={14} />}
              {copied ? "Copied" : "Copy"}
            </button>
          </div>
        </div>
        <div className="p-4 border-t flex justify-end">
          <button type="button" className="btn btn-ghost" onClick={onClose} data-testid="webhook-reveal-close">
            I've stored it safely
          </button>
        </div>
      </div>
    </div>
  );
}
